import { useCallback, useEffect, useState } from 'react';

import type { ProductKeys3P as ProductKeys3PType } from '@atlassian/rovo-connectors-config/types';

import type { OutboundAuthAccountsPayload, SmartLinkEnv } from '../../utils/outboundAuth';
import {
	buildSmartLinkAuthUrl,
	detectEnvFromPayload,
	fetchOutboundAuthAccounts,
	hasSmartLinkContainerMapping,
	isAuthenticatedForProduct,
} from '../../utils/outboundAuth';

export interface UseSmartLinkConnectorByKeyOptions {
	/** Atlassian Account ID. In Confluence, this is `useSessionData().userId`. */
	aaId: string | undefined;
	productKey: ProductKeys3PType;
}

export interface UseSmartLinkConnectorByKeyResult {
	/** Smart-Link-scoped OAuth URL, or `undefined` if the product has no canonical container mapping in this env. */
	authUrl: string | undefined;
	isAuthenticated: boolean;
	isLoading: boolean;
	/** Call after `auth(authUrl)` resolves: optimistic flip + force-refresh of the outbound-auth cache. */
	onConnected: () => void;
}

/**
 * Explicit single-connector lookup. Sidesteps the recommendation cascade
 * used by `use3pConnectorResources`, so the requested product can never be
 * dropped by top-N slicing. Reads outbound-auth (shared 30-min cache) for
 * the auth state and builds the Smart Link OAuth URL from the canonical
 * container map.
 */
export const useSmartLinkConnectorByKey = ({
	aaId,
	productKey,
}: UseSmartLinkConnectorByKeyOptions): UseSmartLinkConnectorByKeyResult => {
	const [payload, setPayload] = useState<OutboundAuthAccountsPayload | null>(null);
	const [env, setEnv] = useState<SmartLinkEnv | undefined>(undefined);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [optimisticAuthed, setOptimisticAuthed] = useState<boolean>(false);

	useEffect(() => {
		setOptimisticAuthed(false);

		let cancelled = false;
		setIsLoading(true);

		void fetchOutboundAuthAccounts(aaId)
			.then((fetched) => {
				if (cancelled) {
					return;
				}
				setPayload(fetched);
				setEnv(detectEnvFromPayload(fetched) ?? undefined);
				setIsLoading(false);
			})
			.catch(() => {
				if (cancelled) {
					return;
				}
				setPayload(null);
				setEnv(undefined);
				setIsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [aaId, productKey]);

	// Auto-heal optimistic flip once ground truth arrives: clears stale
	// optimism even if the OAuth popup closed without granting scopes.
	useEffect(() => {
		if (payload && optimisticAuthed) {
			setOptimisticAuthed(false);
		}
	}, [payload, optimisticAuthed]);

	const onConnected = useCallback(() => {
		if (!aaId) {
			return;
		}
		setOptimisticAuthed(true);
		// Bypass the 30-min cache so a later re-mount sees the fresh grant
		// instead of the stale pre-OAuth payload.
		void fetchOutboundAuthAccounts(aaId, { forceRefresh: true }).then((fresh) => {
			if (fresh) {
				setPayload(fresh);
				setEnv(detectEnvFromPayload(fresh) ?? undefined);
			}
		});
	}, [aaId]);

	const authUrl = hasSmartLinkContainerMapping(productKey, env)
		? (buildSmartLinkAuthUrl(productKey, env) ?? undefined)
		: undefined;

	return {
		authUrl,
		isAuthenticated: optimisticAuthed || isAuthenticatedForProduct(payload, productKey, env),
		isLoading,
		onConnected,
	};
};
